/**
 * Main Application Component
 * 
 * Root component defining application routing structure. Configures React Router
 * with public authentication routes and protected private routes. Wraps the
 * application with context providers for theme, language, toast notifications,
 * and search functionality.
 */

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { lazyWithRetry as lazy } from '@/shared/utils/lazyWithRetry.js';

// Lazy load page components for code splitting and performance optimization
// Auth pages (loaded on demand) - these are default exports from individual page files
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage.jsx'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage.jsx'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage.jsx'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage.jsx'));
const ResetPasswordOTPPage = lazy(() => import('@/features/auth/pages/ResetPasswordOTPPage.jsx'));
const TwoFactorAuthPage = lazy(() => import('@/features/auth/pages/TwoFactorAuthPage.jsx'));
const EmailVerificationSuccessPage = lazy(() => import('@/features/auth/pages/EmailVerificationSuccessPage.jsx'));
const VerifyEmailPage = lazy(() => import('@/features/auth/pages/VerifyEmailPage.jsx'));

// Main feature pages (loaded on demand) - all are default exports from page files
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage.jsx'));
const DisbursementsPage = lazy(() => import('@/features/disbursements/pages/DisbursementsPage.jsx'));
const InvoicingPage = lazy(() => import('@/features/invoicing/pages/InvoicingPage.jsx'));
const PayrollPage = lazy(() => import('@/features/payroll/pages/PayrollPage.jsx'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage.jsx'));
const SettlementsPage = lazy(() => import('@/features/settlements/pages/SettlementsPage.jsx'));
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage.jsx'));
const SMSPage = lazy(() => import('@/features/sms/pages/SMSPage.jsx'));
const CollectionsPage = lazy(() => import('@/features/collections/pages/CollectionsPage.jsx'));
const CheckoutPage = lazy(() => import('@/features/checkout/pages/CheckoutPage.jsx'));
const UtilitiesPage = lazy(() => import('@/features/utilities/pages/UtilitiesPage.jsx'));

// Onboarding pages (loaded on demand)
const UserTypeSelection = lazy(() => import('@/features/onboarding/pages/UserTypeSelection.jsx'));
const IndividualSignUp = lazy(() => import('@/features/onboarding/pages/Individual/IndividualSignUp.jsx'));
const BusinessInfoForm = lazy(() => import('@/features/onboarding/pages/Business/BusinessInfoForm.jsx'));

// Settings sub-pages (loaded on demand)
const IndividualSettings = lazy(() => import('@/features/settings/pages/Individual/Settings.jsx'));
const BusinessSettings = lazy(() => import('@/features/settings/pages/Business/Settings.jsx'));
const IndividualVerification = lazy(() => import('@/features/settings/pages/Individual/Verification.jsx'));
const BusinessVerification = lazy(() => import('@/features/settings/pages/Business/Verification.jsx'));

// Shared components
import { Header, Sidebar, ProtectedRoute, ScrollToTop, LoadingSpinner, OfflineBanner } from '@/shared/components';
import { TourGuide } from '@/shared/components';

// App providers
import {
  ToastProvider,
  ThemeProvider,
  SearchProvider,
  LanguageProvider,
  TourProvider,
  PermissionsProvider
} from '@/app/providers';
import InactivityProvider from '@/app/providers/InactivityProvider.jsx';
import { usePermissions } from '@/app/providers/PermissionsContext.jsx';

// App config
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/app/config/react-query.js';

import '/styles/index.css';

const ErrorPreview = () => {
  throw new Error('Error boundary preview');
};

/**
 * Protects private routes by checking authentication status.
 * Redirects to login page if user is not authenticated.
 * Also waits for permissions to load before showing content.
 */
function PrivateRoute({ children }) {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const { isInitialized } = usePermissions();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wait for permissions to load before showing sidebar and content
  // This prevents team members from seeing pages they shouldn't access
  if (!isInitialized) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: '16px',
        backgroundColor: 'var(--background)'
      }}>
        <LoadingSpinner size="large" />
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{
            fontSize: '16px',
            color: 'var(--text-muted)'
          }}
        >
          Loading...
        </div>
      </div>
    );
  }

  return children;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <ThemeProvider>
          <ToastProvider>
            <SearchProvider>
              <PermissionsProvider>
                <InactivityProvider timeoutMinutes={5} warningMinutes={2} enabled>
                  <Router>
                    <TourProvider>
                      <TourGuide>
                        {/* Offline detection banner */}
                        <OfflineBanner />
                        {/* Skip to main content link for keyboard users */}
                        <a href="#main-content" className="skip-to-main">
                          Skip to main content
                        </a>
                        <ScrollToTop />
                        <Suspense fallback={
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100vh',
                            gap: '16px',
                            backgroundColor: 'var(--background)'
                          }}>
                            <LoadingSpinner size="large" />
                            <div
                              role="status"
                              aria-live="polite"
                              aria-atomic="true"
                              style={{
                                fontSize: '16px',
                                color: 'var(--text-muted)'
                              }}
                            >
                              Loading...
                            </div>
                          </div>
                        }>
                          <Routes>
                            <Route path="/" element={<Navigate to="/login" replace />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/register" element={<RegisterPage />} />
                            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                            <Route path="/reset-password-otp" element={<ResetPasswordOTPPage />} />
                            <Route path="/reset-password" element={<ResetPasswordPage />} />
                            <Route path="/two-factor-auth" element={<TwoFactorAuthPage />} />
                            <Route path="/email-verified" element={<EmailVerificationSuccessPage />} />
                            <Route path="/verify-email" element={<VerifyEmailPage />} />
                            <Route path="/user-type-selection" element={<UserTypeSelection />} />
                            <Route path="/signup-individual" element={<IndividualSignUp />} />
                            <Route path="/signup-business" element={<BusinessInfoForm />} />
                            <Route path="/signup-agent" element={<BusinessInfoForm />} />
                            {import.meta.env.DEV && (
                              <Route path="/error-preview" element={<ErrorPreview />} />
                            )}

                            <Route element={
                              <PrivateRoute>
                                <div className="dashboard-container">
                                  <Sidebar />
                                  <div className="dashboard-main">
                                    <Header />
                                    <div id="main-content" className="dashboard-content" tabIndex={-1}>
                                      <Outlet />
                                    </div>
                                  </div>
                                </div>
                              </PrivateRoute>
                            }>
                              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                              <Route path="/disbursements" element={<ProtectedRoute><DisbursementsPage /></ProtectedRoute>} />
                              <Route path="/utilities" element={<ProtectedRoute><UtilitiesPage /></ProtectedRoute>} />
                              <Route path="/invoicing" element={<ProtectedRoute><InvoicingPage /></ProtectedRoute>} />
                              <Route path="/collections" element={<ProtectedRoute><CollectionsPage /></ProtectedRoute>} />
                              <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
                              <Route path="/settlements" element={<ProtectedRoute><SettlementsPage /></ProtectedRoute>} />
                              <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
                              <Route path="/SMS" element={<ProtectedRoute><SMSPage /></ProtectedRoute>} />
                              <Route path="/payroll" element={<ProtectedRoute><PayrollPage /></ProtectedRoute>} />

                              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>}>
                                <Route path="individual" element={<IndividualSettings />}>
                                  <Route path="verification" element={<IndividualVerification />} />
                                </Route>
                                <Route path="business" element={<BusinessSettings />}>
                                  <Route path="verification" element={<BusinessVerification />} />
                                </Route>
                              </Route>

                              {/* Catch-all route for authenticated users */}
                              <Route path="*" element={<Navigate to="/dashboard" replace />} />
                            </Route>

                            {/* Catch-all route for unauthenticated users */}
                            <Route path="*" element={<Navigate to="/login" replace />} />
                          </Routes>
                        </Suspense>
                      </TourGuide>
                    </TourProvider>
                  </Router>
                </InactivityProvider>
              </PermissionsProvider>
            </SearchProvider>
          </ToastProvider>
        </ThemeProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}
